import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';

export interface UserModalData {
    id?: string;
    username?: string;
    name?: string;
    mobile?: string;
    email?: string;
    active?: boolean;
    isAdmin?: boolean;
    canAccessOrders?: boolean;
    categoryIds?: string[];
}

export interface CategoryOption {
    id: string;
    categoryName: string;
}

/**
 * Add / edit user modal. Prefilled when a `user` is passed (edit); empty for
 * add. Returns the form payload via activeModal.close(); the caller performs
 * the create/update + toast.
 *
 * Password mirrors the legacy form: typing a value sets it, leaving it blank on
 * edit leaves the existing password unchanged. It is hashed server-side.
 */
@Component({
    selector: 'app-user-modal',
    imports: [FormsModule, NgSelectModule, NgxTgModule],
    templateUrl: './user-modal.component.html',
    styleUrl: './user-modal.component.scss',
})
export class UserModalComponent implements OnInit {
    @Input() user: UserModalData | null = null;
    @Input() categories: CategoryOption[] = [];

    id?: string;
    username = '';
    password = '';
    confirmPassword = '';
    name = '';
    mobile = '';
    email = '';
    // New users are created enabled: an admin creating an account and handing
    // over the credentials expects them to work. Editing an existing user
    // overwrites this from the row below.
    active = true;
    isAdmin = false;
    canAccessOrders = false;
    categoryIds: string[] = [];
    error: string | null = null;

    // Per-field invalid flags (red border) + a shake pulse.
    usernameInvalid = false;
    passwordInvalid = false;
    emailInvalid = false;
    shaking = false;

    private static readonly EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    constructor(private i18n: TranslateService, public activeModal: NgbActiveModal) { }

    get title(): string {
        return this.i18n.instant(this.id ? 'user_modal_title_edit' : 'user_modal_title_add');
    }

    ngOnInit(): void {
        if (this.user) {
            this.id = this.user.id;
            this.username = this.user.username || '';
            this.name = this.user.name || '';
            this.mobile = this.user.mobile || '';
            this.email = this.user.email || '';
            this.active = !!this.user.active;
            this.isAdmin = !!this.user.isAdmin;
            this.canAccessOrders = !!this.user.canAccessOrders;
            this.categoryIds = [...(this.user.categoryIds || [])];
            // password is never prefilled — blank means "leave unchanged".
        }
    }

    save(): void {
        this.error = null;
        this.usernameInvalid = false;
        this.passwordInvalid = false;
        this.emailInvalid = false;
        let bad = false;

        if (!this.username.trim()) {
            this.usernameInvalid = true;
            this.error = this.i18n.instant('user_modal_error_username');
            bad = true;
        }
        if ((this.password || this.confirmPassword) && this.password !== this.confirmPassword) {
            this.passwordInvalid = true;
            this.error = this.i18n.instant('validation_password_mismatch');
            bad = true;
        }
        if (this.email && !UserModalComponent.EMAIL_RE.test(this.email.trim())) {
            this.emailInvalid = true;
            this.error = this.i18n.instant('validation_email_invalid');
            bad = true;
        }
        if (bad) {
            this.triggerShake();
            return;
        }

        const payload: UserModalData & { password?: string } = {
            id: this.id,
            username: this.username.trim(),
            name: this.name.trim(),
            mobile: this.mobile.trim(),
            email: this.email.trim(),
            active: this.active,
            isAdmin: this.isAdmin,
            canAccessOrders: this.canAccessOrders,
            categoryIds: this.categoryIds || [],
        };
        // Only send a password when one was actually typed.
        if (this.password) {
            payload.password = this.password;
        }
        this.activeModal.close(payload);
    }

    private triggerShake(): void {
        this.shaking = true;
        setTimeout(() => { this.shaking = false; }, 400);
    }
}
